import { useLayoutEffect, useRef, useState } from 'react';

import { BaseBottomSheet } from '@picsart/design-system/components/BaseBottomSheet';
import { Button } from '@picsart/design-system/components/Button';
import { InlineButton } from '@picsart/design-system/components/InlineButton';

import { useTranslator } from '@picsart/rc/services/localization';
import useStyles from './styles';

import type { FeedSheetLayoutProps } from './types';

// Pre-measurement estimate for the first open; the ResizeObserver below
// replaces it with the real content height.
const SHEET_FALLBACK_HEIGHT = 430;

// BaseBottomSheet renders a 12px drag-handle row above the content area.
const SHEET_DRAG_HANDLE_HEIGHT = 12;

const FeedSheetLayout = ({
  isOpen,
  onClose,
  title,
  subtitle,
  primaryLabel,
  isPrimaryDisabled,
  isPrimaryLoading = false,
  onPrimaryAction,
  primaryTestId,
  cancelTestId,
  ariaLabel,
  children,
}: FeedSheetLayoutProps) => {
  const classes = useStyles();
  const translate = useTranslator();

  const measureRef = useRef<HTMLDivElement>(null);
  const [sheetHeight, setSheetHeight] = useState(SHEET_FALLBACK_HEIGHT);

  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    const node = measureRef.current;
    if (!node) return undefined;
    const observer = new ResizeObserver(() => {
      setSheetHeight(node.offsetHeight + SHEET_DRAG_HANDLE_HEIGHT);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [isOpen]);

  return (
    <BaseBottomSheet
      isOpen={isOpen}
      onClose={onClose}
      snapPoints={[sheetHeight]}
      defaultActiveIndex={0}
      ariaLabel={ariaLabel}
    >
      <div className={classes.scrollArea}>
        <div ref={measureRef} className={classes.content}>
          <h2 className={classes.title}>{title}</h2>
          <p className={classes.subtitle}>{subtitle}</p>

          <div className={classes.body}>{children}</div>

          <Button
            dataTestId={primaryTestId}
            className={classes.primaryButton}
            variant="filled"
            size="large"
            isFullWidth
            isDisabled={isPrimaryDisabled || isPrimaryLoading}
            isLoading={isPrimaryLoading}
            onClick={onPrimaryAction}
          >
            {primaryLabel}
          </Button>

          <InlineButton
            dataTestId={cancelTestId}
            className={classes.cancelButton}
            size="large"
            isWithoutBorder
            isWithoutBackground
            onClick={onClose}
          >
            {translate('Cancel')}
          </InlineButton>
        </div>
      </div>
    </BaseBottomSheet>
  );
};

export default FeedSheetLayout;
